import { apiFetch, isApiMode } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE PLATFORM RATING CORRECTION, AS /sa SEES IT (#793).
//
// `POST /players/:id/elo-adjustment` is platform authority since #197: the
// player table has no tenant column, so «correct this rating» is «correct it
// for every club», and only `rating.adjust_manual` at PLATFORM scope reaches
// it. The endpoint, its audit row and its guard already exist; this is the
// interface to them, and it adds no rule of its own about what a rating may
// be. The server decides — the screen shows what it decided.
//
// WHY NOT `fetchEloAdjustments` / `adjustPlayerElo` from `players.ts`:
//
//   • `fetchEloAdjustments` answers `[]` for a failed read. On a screen whose
//     job is to show a player's correction history, «no corrections» and
//     «could not read the log» must not look the same — the same lesson
//     `fetchPlayers` learned in #272.
//   • `adjustPlayerElo` turns a refusal into a Norwegian SENTENCE thrown as an
//     exception, written before the catalog existed. This surface is born on
//     the catalog, so it needs the CODE: the browser picks the words, in the
//     reader's language, and nothing translated travels anywhere.
//
// Same wire contract, same route, same body. Everything here is a VIEW of
// what the server decided, in the style of the other /sa clients: it never
// throws, and every outcome is a state the section renders.
// ─────────────────────────────────────────────────────────────────────

/** A player as `GET /players` returns them — only the fields this screen reads. */
export interface RatingPlayer {
  /** The app's id: a NUMBER for seed players, a string for custom ones. */
  id: string | number;
  name: string;
  elo?: number;
  level?: number;
  matchesPlayed?: number;
  isSystem?: boolean;
}

/** One row of the append-only correction log (`GET /elo-adjustments`). */
export interface RatingAdjustment {
  id: string;
  /** Same id space as `RatingPlayer.id` — the server restores both alike. */
  playerId: string | number;
  playerName: string;
  oldElo: number;
  newElo: number;
  change: number;
  adjustedAt: number;
  /** NULL on rows written while a reason was optional — history, not rewritten. */
  reason: string | null;
}

export type RatingDataResult =
  | { state: 'no-api' }
  | { state: 'ok'; players: RatingPlayer[]; adjustments: RatingAdjustment[] }
  /** The server answered with a refusal; `code` is its machine code, if any. */
  | { state: 'refused'; status: number; code: string | null }
  | { state: 'error'; status: number | null };

async function refusal(res: Response): Promise<{ code: string | null; minimum?: number }> {
  const body = (await res.json().catch(() => null)) as {
    error?: unknown;
    minimum?: unknown;
  } | null;
  return {
    code: typeof body?.error === 'string' ? body.error : null,
    // `reason_required` says how long a reason the server wants. Carried so
    // the sentence quotes the server's number, not a copy of it.
    ...(typeof body?.minimum === 'number' ? { minimum: body.minimum } : {}),
  };
}

/**
 * The player list and the correction log, together.
 *
 * Both are needed before anything is offered: a correction screen that shows a
 * rating without its history invites somebody to «fix» a value another
 * operator corrected on purpose yesterday.
 */
export async function loadRatingData(): Promise<RatingDataResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const [playersRes, logRes] = await Promise.all([
      apiFetch('/players'),
      apiFetch('/elo-adjustments'),
    ]);
    for (const res of [playersRes, logRes]) {
      if (res.ok) continue;
      if (res.status >= 500) return { state: 'error', status: res.status };
      return { state: 'refused', status: res.status, code: (await refusal(res)).code };
    }
    return {
      state: 'ok',
      players: (await playersRes.json()) as RatingPlayer[],
      adjustments: (await logRes.json()) as RatingAdjustment[],
    };
  } catch {
    return { state: 'error', status: null };
  }
}

export type CorrectionResult =
  | { state: 'no-api' }
  | { state: 'ok'; player: RatingPlayer; adjustment: RatingAdjustment }
  /**
   * The server said no — or, for `no_change`, said «that is already the
   * rating». The section decides which of those is an error; this only
   * carries the code.
   */
  | { state: 'refused'; status: number; code: string | null; minimum?: number }
  | { state: 'error'; status: number | null };

/**
 * Set a player's rating by hand, with the reason the audit log keeps.
 *
 * Sends ONLY `newElo` and `reason`. The server derives the old value and the
 * change from the row it holds, under the rating lock — a client that could
 * name them could write a record that disagrees with the rating it explains.
 */
export async function correctRating(
  playerId: string | number,
  newElo: number,
  reason: string,
): Promise<CorrectionResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch(`/players/${encodeURIComponent(String(playerId))}/elo-adjustment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newElo, reason }),
    });
    if (res.ok) {
      const body = (await res.json()) as { player: RatingPlayer; adjustment: RatingAdjustment };
      return { state: 'ok', player: body.player, adjustment: body.adjustment };
    }
    if (res.status >= 500) return { state: 'error', status: res.status };
    return { state: 'refused', status: res.status, ...(await refusal(res)) };
  } catch {
    return { state: 'error', status: null };
  }
}

/**
 * The server's minimum, mirrored so the form can say it before anyone presses
 * the button. NOT the rule: `routes/players.ts` refuses a shorter reason with
 * `reason_required` whatever this says, and the screen renders that refusal.
 */
export const MIN_REASON_LENGTH = 3;
